import React from 'react';
import {
  Button,
  Drawer,
  Menu,
  Popover,
  Tabs,
  TabsItemProps,
  useToasts,
} from 'freemium-ui';
import { History } from 'history';
import { get, isNil } from 'lodash';
import qs from 'query-string';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { BACK_END_FIELDS } from 'shared/constants';
import { useAccessControl } from 'utils/access-control';
import {
  AccountInfoType,
  ConfirmationType,
  ContactFormType,
  FormServFieldType,
  McrContactType,
} from '../../../../../shared/types';
import Icon from '../../../../../shared/components/Icon';
import { TabContainer } from '../../../../../shared/styles';
import NewContactForm from '../../../../components/NewButton/NewContactForm';
import { useDeleteContact, useSaveContact } from '../../hooks';
import { getDisplayValueFromField } from '../../../../utils';
import {
  ContactCard,
  ContactDetailsTable,
  DetailsPane,
  ListDetailsContainer,
  SummaryPane,
  ViewContactContainer,
} from '../styles';
import { LEFT_PANE_FIELD_NAMES, getContactMenuOptions } from '../../constants';
import { tabOptions } from '../constants';
import ContactDetails from '../ContactDetails';
import ContactListName from '../ContactListName';
import RecentResponses from '../RecentResponses';

type ContactDisplayProps = {
  contactForm: ContactFormType;
  contactId: string;
  contactData: McrContactType;
  accountInfo?: AccountInfoType;
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  refetchContacts: () => void;
};

const ContactDisplay = (props: ContactDisplayProps) => {
  const { t, i18n } = useTranslation();
  const {
    contactForm,
    contactData,
    accountInfo,
    setConfirmationData,
    refetchContacts,
  } = props;
  const { push }: History = useHistory();
  const routerLocation = useLocation();
  const [, setToast] = useToasts();
  const { access } = useAccessControl();
  const userHasAccess = React.useMemo(() => {
    return (access || []).indexOf('contact:write') > -1;
  }, [access]);

  const fields =
    contactForm && contactForm.fields && contactForm.fields.length > 0
      ? contactForm.fields.filter(
          (contact) => !BACK_END_FIELDS.includes(contact.name),
        )
      : [];
  const allFields: { [key: string]: FormServFieldType } = {};
  fields.forEach((field) => {
    allFields[field.name] = field;
  });

  const [selectedTab, setSelectedTab] = React.useState<string>(
    tabOptions[0].id,
  );
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const [editData, setEditData] = React.useState<any>({});
  const [anchorEl, setAnchorEl] = React.useState<any>(null);

  const { mutate: saveContact } = useSaveContact({
    setToast,
    callback: (isSuccess) => {
      if (isSuccess) {
        setConfirmationData({});
      } else {
        setConfirmationData((old) => ({
          ...old,
          confirmButtonDisabled: false,
        }));
        setToast({
          type: 'error',
          delay: 3000,
          text: `${t('forms.toast.error.contact_update')}`,
        });
      }
    },
  });

  const { mutate: deleteContact } = useDeleteContact({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        setConfirmationData({});
        push('/contacts');
      } else {
        setConfirmationData((old) => ({
          ...old,
          confirmButtonDisabled: false,
        }));
      }
    },
  });

  React.useEffect(() => {
    if (!routerLocation || !routerLocation.search) return;
    const parsedValues = qs.parse(routerLocation.search);
    const { tab, ...rest }: { tab?: 'recentResponses' | 'details' } =
      parsedValues;
    if (!tab) return;
    if (tabOptions.find((tabOption) => tabOption.id === tab))
      setSelectedTab(tab || 'details');
    if (tab || Object.keys(rest).length > 0) {
      const newURL = qs.stringifyUrl({
        url: routerLocation.pathname,
        query: { ...(rest || {}) },
      });
      push(newURL);
    }
    refetchContacts();
  }, [routerLocation.search]);

  const options = getContactMenuOptions(contactData, ['view'], access);

  const avatar = React.useMemo(
    () =>
      get(
        contactData,
        ['first_name', 0],
        get(
          contactData,
          ['last_name', 0],
          get(contactData, ['work_email', 0], ''),
        ),
      ) || '',
    [contactData],
  );

  return (
    <ViewContactContainer>
      {userHasAccess && (
        <Drawer
          isOpen={Boolean(editData.id)}
          onDismiss={() => {
            if (isDirty) {
              setConfirmationData({
                id: new Date().toISOString(),
                title: `${t('forms.drawer.contact_title')}`,
                onConfirm: () => {
                  setEditData({});
                  setConfirmationData({});
                },
                onDismiss: () => setConfirmationData({}),
                description: `${t('forms.drawer.contact_desc')}`,
              });
            } else {
              setEditData({});
            }
          }}
          isFullHeight
          clickOutToClose={false}
          size="large"
          placement="right"
        >
          <NewContactForm
            fields={fields}
            setIsDirty={(d: boolean) => setIsDirty(d)}
            values={editData}
            toggleDrawer={() => setEditData({})}
          />
        </Drawer>
      )}
      <SummaryPane>
        <ContactCard alphabet={avatar}>
          <div className="avatar">{avatar}</div>
          <div className="details">
            <div className="name overflow">
              {`${contactData?.first_name || ''} ${
                contactData?.last_name || ''
              }`.trim()}
            </div>
            <div className="email overflow">{contactData.work_email}</div>
          </div>
          {userHasAccess && (
            <div className="edit-button">
              <Button
                inline
                onClick={(e: React.MouseEvent) => setAnchorEl(e.currentTarget)}
                aria-describedby="contact-actions-menu"
                type="secondary"
              >
                <span>{t('workflow.table_header.actions')}</span>
                <Icon fill="#1234dd" glyph="arrow-down" width="8" height="8" />
              </Button>
              <Popover
                id="contact-actions-menu"
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Menu
                  options={options}
                  onSelect={({ id }: any) => {
                    switch (id) {
                      case 'edit':
                        setEditData(contactData);
                        break;
                      case 'delete':
                        setConfirmationData({
                          id: new Date().toISOString(),
                          title: `${t('contacts.menu.delete.title')}`,
                          className: 'error',
                          confirmButtonText: `${t('delete')}`,
                          onConfirm: () => {
                            setConfirmationData((old) => ({
                              ...old,
                              confirmButtonDisabled: true,
                            }));
                            deleteContact(contactData.id || '');
                          },
                          data: contactData,
                          fill: '#d7461f',
                          infoText: `${t('contacts.menu.delete.infotext')}`,
                          description: (
                            <>
                              <Trans
                                i18nKey="contacts.menu.delete.desc"
                                values={{
                                  email: contactData.work_email,
                                }}
                                components={{ bold: <b /> }}
                              />
                            </>
                          ),
                          glyph: 'info',
                        });
                        break;
                      case 'activate':
                        setConfirmationData({
                          id: new Date().toISOString(),
                          confirmButtonText: `${t(
                            'contacts.menu.activate.text',
                          )}`,
                          onConfirm: () => {
                            setConfirmationData((old) => ({
                              ...old,
                              confirmButtonDisabled: true,
                            }));
                            saveContact({
                              payload: {
                                ...contactData,
                                fields: {
                                  ...(contactData.fields || {}),
                                  contact_status: 'ACTIVE',
                                },
                              },
                            });
                          },
                          title: `${t('contacts.menu.activate.title')}`,
                          description: (
                            <>
                              <Trans
                                i18nKey="contacts.menu.activate.desc"
                                values={{
                                  email: contactData.work_email,
                                }}
                                components={{ bold: <b /> }}
                              />
                            </>
                          ),
                        });
                        break;
                      case 'deactivate':
                        setConfirmationData({
                          id: new Date().toISOString(),
                          confirmButtonText: `${t(
                            'contacts.menu.deactivate.text',
                          )}`,
                          onConfirm: () => {
                            setConfirmationData((old) => ({
                              ...old,
                              confirmButtonDisabled: true,
                            }));
                            saveContact({
                              payload: {
                                ...contactData,
                                fields: {
                                  ...(contactData.fields || {}),
                                  contact_status: 'IN_ACTIVE',
                                },
                              },
                            });
                          },
                          title: `${t('contacts.menu.deactivate.title')}`,
                          description: (
                            <>
                              <Trans
                                i18nKey="contacts.menu.deactivate.desc"
                                values={{
                                  email: contactData.work_email,
                                }}
                                components={{ bold: <b /> }}
                              />
                            </>
                          ),
                        });
                        break;
                      // case 'unblock':
                      //   setConfirmationData({
                      //     id: new Date().toISOString(),
                      //     confirmButtonText: 'Unblock',
                      //     onConfirm: () => {
                      //       setConfirmationData((old) => ({
                      //         ...old,
                      //         confirmButtonDisabled: true,
                      //       }));
                      //       saveContact({
                      //         payload: {
                      //           ...contactData,
                      //           fields: {
                      //             ...(contactData.fields || {}),
                      //             is_blocked: false,
                      //           },
                      //         },
                      //       });
                      //     },
                      //     title: 'Confirm unblock',
                      //     description: `Do you want to unblock ${contactData.work_email}?`,
                      //   });
                      //   break;
                      // case 'block':
                      //   setConfirmationData({
                      //     id: new Date().toISOString(),
                      //     confirmButtonText: 'Block',
                      //     onConfirm: () => {
                      //       setConfirmationData((old) => ({
                      //         ...old,
                      //         confirmButtonDisabled: true,
                      //       }));
                      //       saveContact({
                      //         payload: {
                      //           ...contactData,
                      //           fields: {
                      //             ...(contactData.fields || {}),
                      //             is_blocked: true,
                      //           },
                      //         },
                      //       });
                      //     },
                      //     title: 'Confirm block',
                      //     description: `Do you want to block ${contactData.work_email}?`,
                      //   });
                      //   break;
                      case 'unsubscribe':
                        setConfirmationData({
                          id: new Date().toISOString(),
                          confirmButtonText: `${t(
                            'contacts.menu.unsubscribe.text',
                          )}`,
                          onConfirm: () => {
                            setConfirmationData((old) => ({
                              ...old,
                              confirmButtonDisabled: true,
                            }));
                            saveContact({
                              payload: {
                                ...contactData,
                                fields: {
                                  ...(contactData.fields || {}),
                                  is_unsubscribed: true,
                                  unsubscribe_time: new Date().toISOString(),
                                },
                              },
                            });
                          },
                          title: `${t('contacts.menu.unsubscribe.title')}`,
                          description: (
                            <>
                              <Trans
                                i18nKey="contacts.menu.unsubscribe.desc"
                                values={{
                                  email: contactData.work_email,
                                }}
                                components={{ bold: <b /> }}
                              />
                            </>
                          ),
                        });
                        break;
                      case 'subscribe':
                        setConfirmationData({
                          id: new Date().toISOString(),
                          confirmButtonText: `${t(
                            'contacts.menu.subscribe.text',
                          )}`,
                          onConfirm: () => {
                            setConfirmationData((old) => ({
                              ...old,
                              confirmButtonDisabled: true,
                            }));
                            saveContact({
                              payload: {
                                ...contactData,
                                fields: {
                                  ...(contactData.fields || {}),
                                  is_unsubscribed: false,
                                  unsubscribe_time: null,
                                },
                              },
                            });
                          },
                          title: `${t('contacts.menu.subscribe.title')}`,
                          description: (
                            <>
                              <Trans
                                i18nKey="contacts.menu.subscribe.desc"
                                values={{
                                  email: contactData.work_email,
                                }}
                                components={{ bold: <b /> }}
                              />
                            </>
                          ),
                        });
                        break;
                      default:
                    }
                  }}
                  closeMenuCallback={() => setAnchorEl(null)}
                />
              </Popover>
            </div>
          )}
        </ContactCard>
        <div className="details-table">
          <ContactDetailsTable>
            <tbody>
              {LEFT_PANE_FIELD_NAMES.map(
                (fieldName: string) => allFields[fieldName],
              )
                .filter((field: FormServFieldType) => !isNil(field))
                .map((field: FormServFieldType) => {
                  const value = field.custom
                    ? get(contactData, ['fields', field.name], '')
                    : get(contactData, [field.name], '');
                  const fieldId = get(field?.choices?.[value], ['id'], value);
                  return (
                    <tr key={field.id}>
                      <td className="key">
                        {i18n.exists(
                          `contacts.import_contact.default_fields.contact.${field.name}`,
                        )
                          ? `${t(
                              `contacts.import_contact.default_fields.contact.${field.name}`,
                            )}`
                          : field.label}
                      </td>
                      <td className="value">
                        {i18n.exists(
                          `contacts.import_contact.form_choices.${field.name}.${fieldId}`,
                        )
                          ? `${t(
                              `contacts.import_contact.form_choices.${field.name}.${fieldId}`,
                            )}`
                          : getDisplayValueFromField({
                              field,
                              rowValues: contactData,
                              // LEFT_PANE_FIELD_NAMES has no list_ids
                              contactLists: [],
                              accountInfo,
                            })}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </ContactDetailsTable>
        </div>
        {get(contactData, ['fields', 'list_ids'], []).length > 0 && (
          <ListDetailsContainer>
            <div className="header">Lists</div>
            <div className="list">
              {get(contactData, ['fields', 'list_ids'], []).map(
                (displayId: string) => (
                  <div
                    key={displayId}
                    className="item"
                    onClick={() => push(`/lists/${displayId}`)}
                  >
                    <ContactListName
                      id={displayId}
                      contactId={contactData.id || ''}
                    />
                  </div>
                ),
              )}
            </div>
          </ListDetailsContainer>
        )}
      </SummaryPane>
      <DetailsPane>
        <TabContainer>
          <Tabs
            value={selectedTab}
            onChange={(
              event: React.MouseEvent<HTMLDivElement>,
              tab: TabsItemProps,
            ) => setSelectedTab(tab.id)}
            options={tabOptions.map((eOption) => ({
              ...eOption,
              name: `${t(eOption.name)}`,
            }))}
          />
        </TabContainer>
        <>
          {selectedTab === 'details' && (
            <div className="contact-details-container">
              <ContactDetails
                contactForm={contactForm}
                contactData={contactData}
                accountInfo={accountInfo}
              />
            </div>
          )}
          {selectedTab === 'recentResponses' && (
            <RecentResponses
              contactData={contactData}
              accountInfo={accountInfo}
            />
          )}
        </>
      </DetailsPane>
    </ViewContactContainer>
  );
};

export default ContactDisplay;
